/**
 * App.tsx - Application Root (Composition Root)
 * 
 * This file is the composition root for the application.
 * It sets up Ionic, provides context providers, mounts AppContent,
 * and initializes telemetry (PostHog + Sentry).
 * 
 * All run lifecycle state, handlers, and screen rendering are in AppContent.tsx.
 */
import { lazy, Suspense, useState, useEffect } from 'react';
import { setupIonicReact, IonApp, IonSpinner } from '@ionic/react';
import { IonReactRouter } from '@ionic/react-router';
import { UserProvider } from '../contexts/UserContext';
import { TeamStartProvider } from '../contexts/TeamStartContext';
import { RunOrchestrationProvider } from '../run-orchestration';
import { Toaster } from '../components/ui/feedback/sonner';
import { SafeAreaDebug } from '../screens/pages/shared/SafeAreaDebug';
import { SentryErrorBoundary } from '../app-core/analytics/sentry';
import { AnalyticsConsentBanner } from '../components/modals/AnalyticsConsentBanner';

// Lazy load AppContent to prevent Vite module graph deadlock
const AppContent = lazy(() => import('../AppContent'));

// Configure Ionic
setupIonicReact({
  mode: 'ios',
  swipeBackEnabled: true,
});

/**
 * Root wrapper component with providers and Ionic shell
 * This ensures providers are always mounted, even after Figma export
 */
function App() {
  console.log("✅ App root mounted - using Ionic routing only");
  
  const [consentGiven, setConsentGiven] = useState<boolean | null>(null);
  
  // Check if consent already captured
  useEffect(() => {
    const consentShown = localStorage.getItem('analytics_consent_shown');
    if (consentShown) {
      // Consent already captured, don't show banner again
      setConsentGiven(true);
    }
  }, []);
  
  const handleConsentResponse = (accepted: boolean) => {
    setConsentGiven(accepted);
    console.log(`[Analytics] Consent ${accepted ? 'accepted' : 'declined'}`);
  };
  
  return (
    <SentryErrorBoundary
      fallback={({ error, resetError }) => (
        <div style={{ 
          padding: '2rem', 
          textAlign: 'center',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100vh',
          background: '#f5f5f5',
        }}>
          <h1 style={{ fontSize: '1.5rem', marginBottom: '1rem', color: '#d32f2f' }}>
            😞 Oops! Something went wrong
          </h1>
          <p style={{ marginBottom: '1.5rem', color: '#666' }}>
            We&apos;ve been notified and are looking into it.
          </p>
          <button
            onClick={resetError}
            style={{
              padding: '0.75rem 1.5rem',
              fontSize: '1rem',
              background: '#1976d2',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
            }}
          >
            Try Again
          </button>
          {import.meta.env.DEV && (
            <pre style={{ 
              textAlign: 'left', 
              background: '#fff', 
              padding: '1rem', 
              marginTop: '1rem',
              borderRadius: '4px',
              maxWidth: '600px',
              overflow: 'auto',
            }}>
              {error.toString()}
            </pre>
          )}
        </div>
      )}
    >
      <UserProvider>
        <TeamStartProvider>
          <RunOrchestrationProvider>
            <IonApp className="min-h-screen-safe bg-gray-100">
              <IonReactRouter>
                <Suspense fallback={
                  <div className="flex items-center justify-center h-screen">
                    <IonSpinner name="crescent" />
                  </div>
                }>
                  <AppContent />
                </Suspense>
              </IonReactRouter>
            </IonApp>
            <Toaster />
            <SafeAreaDebug />
            
            {/* Analytics Consent Banner - shown once on first launch */}
            {consentGiven === null && (
              <AnalyticsConsentBanner onConsentGiven={handleConsentResponse} />
            )}
          </RunOrchestrationProvider>
        </TeamStartProvider>
      </UserProvider>
    </SentryErrorBoundary>
  );
}

export default App;
